// src/components/patient/PatientSurveyPanel.js
//
// Files → "Health survey" on the patient page (flag PFT_SURVEY_TEXT_ENABLED,
// 2026-10-26): text a patient with no file the health survey (only on a click, never
// automatically), see when they were texted / answered, and pick between RegX and the
// survey where their answers differ. Rules: services/patientSurvey.js and
// functions/pftSurveyPatient.js. Own classes (patient-survey-*) in both modes.

import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, MessageSquare, RefreshCw } from "lucide-react";
import {
  attachPatientSurveyAnswers,
  loadCardSurveyForPatient,
  previewPatientSurveyText,
  resolvePatientSurveyAnswer,
  sendPatientSurveyText,
  watchPatientSurveyStatus,
} from "../../services/patientSurveyService";
import { surveyPanelView } from "../../services/patientSurvey";

/**
 * The patient's survey state: the live status doc, and (only while there is none) a PFT
 * Uploads card texted before they were moved. `patient` = { store, id } or null.
 */
export const usePatientSurvey = (patient) => {
  const [status, setStatus] = useState(null);
  const [card, setCard] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const key = patient ? `${patient.store}__${patient.id}` : "";
  useEffect(() => {
    if (!patient) return undefined;
    let live = true;
    const unsub = watchPatientSurveyStatus(
      patient,
      (s) => {
        if (!live) return;
        setStatus(s);
        setLoaded(true);
      },
      () => live && setLoaded(true),
    );
    loadCardSurveyForPatient(patient)
      .then((c) => live && setCard(c))
      .catch(() => {});
    return () => {
      live = false;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { status, card, loaded };
};

const FIELD_VALUE = { phone: "phone", physicalAddress: "physicalAddress", email: "email" };

/**
 * props: patient ({ store, id }), survey (usePatientSurvey's result), hasFile,
 * current ({ phone, physicalAddress, email }: what the page shows now, sent as `from`),
 * submissionIds (survey submissions already on the record, e.g. from a PFT Uploads move).
 */
export default function PatientSurveyPanel({ patient, survey, hasFile, current = {}, submissionIds = [] }) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  if (!patient || !survey || !survey.loaded) return null;
  const view = surveyPanelView({ status: survey.status, card: survey.card, hasFile, submissionIds });
  if (!view.show) return null;

  const send = async () => {
    setError(null);
    setNotice(null);
    const resend = view.state === "texted";
    setBusy("send");
    try {
      const p = await previewPatientSurveyText(patient, { resend });
      if (p.reason) {
        setError(`Can't text this patient: ${p.reason}.`);
        return;
      }
      const ok = window.confirm(
        `Text the phone ending ${p.phoneLast4} this message?\n\n${p.body.replace("[link]", "(their survey link)")}`,
      );
      if (!ok) return;
      await sendPatientSurveyText(patient, { resend });
      setNotice("Texted. When they answer, their signed consent and answers are added here by themselves.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const decide = async (c, choice) => {
    setError(null);
    setBusy(`${c.field}:${choice}`);
    try {
      await resolvePatientSurveyAnswer(patient, { field: c.field, choice, from: current[FIELD_VALUE[c.field]] || "" });
      setNotice(choice === "survey" ? `${c.label} updated to the survey's answer. RegX's old value is in Legacy Notes.` : `Kept RegX's ${c.label.toLowerCase()}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const attach = async () => {
    setError(null);
    setBusy("attach");
    try {
      const r = await attachPatientSurveyAnswers(patient);
      setNotice(r.alreadyAttached ? "Their answers were already on this patient." : "Their consent and answers were added.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mt-3 rounded-xl border p-3 space-y-2 text-xs patient-survey-panel" data-testid="patient-survey-panel">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold patient-survey-title">Health survey</span>
        {view.state === "responded" && (
          <span className="text-[10px] px-2 py-0.5 rounded-full border pft-survey-chip-responded">Answered</span>
        )}
        {view.state === "texted" && (
          <span className="text-[10px] px-2 py-0.5 rounded-full border pft-survey-chip-texted">Texted, no answer yet</span>
        )}
      </div>
      {view.sentLine && <p className="patient-survey-muted">{view.sentLine}.</p>}
      {view.answeredLine && (
        <p className="patient-survey-muted">
          {view.answeredLine}.
          {view.attachNeeded ? "" : " Their signed consent is under Other files and their answers are in Legacy Notes."}
        </p>
      )}
      {view.state === "none" && (
        <p className="patient-survey-muted">
          This patient has no file. Text them the health questionnaire and consent; when they sign, it's added here by
          itself.
        </p>
      )}

      {view.canSend && (
        <button
          type="button"
          onClick={send}
          disabled={!!busy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition disabled:opacity-50 patient-survey-btn"
        >
          {busy === "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
          {view.buttonLabel}
        </button>
      )}

      {view.attachNeeded && (
        <div className="space-y-1.5">
          <p className="patient-survey-warn">They answered, but their consent and answers aren't on this patient yet.</p>
          <button
            type="button"
            onClick={attach}
            disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition disabled:opacity-50 patient-survey-btn"
          >
            {busy === "attach" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Attach their answers now
          </button>
        </div>
      )}

      {view.open.length > 0 && (
        <div className="rounded-lg border p-2.5 space-y-2 patient-survey-check">
          <p className="font-semibold">Survey answers to check ({view.open.length})</p>
          <p className="patient-survey-muted">These answers differ from what RegX has. Nothing was changed: pick which to keep.</p>
          {view.open.map((c) => (
            <div key={c.field} className="space-y-1.5">
              <p>
                <span className="font-semibold">{c.label}</span>
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => decide(c, "regx")}
                  disabled={!!busy}
                  className="text-left px-2.5 py-1.5 rounded-lg border transition disabled:opacity-50 patient-survey-choice"
                >
                  Keep RegX: {c.regx}
                </button>
                <button
                  type="button"
                  onClick={() => decide(c, "survey")}
                  disabled={!!busy}
                  className="text-left px-2.5 py-1.5 rounded-lg border transition disabled:opacity-50 patient-survey-choice"
                >
                  Use survey: {c.survey}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {view.decided.length > 0 && (
        <ul className="space-y-0.5 patient-survey-muted">
          {view.decided.map((c) => (
            <li key={c.field} className="flex items-start gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-px" />
              {c.label}: {c.resolved.choice === "survey" ? `used the survey's (${c.survey})` : `kept RegX's (${c.regx})`}
              {c.resolved.by ? ` · ${c.resolved.by}` : ""}
            </li>
          ))}
        </ul>
      )}

      {notice && <p className="patient-survey-ok">{notice}</p>}
      {error && <p className="patient-survey-warn">{error}</p>}
    </div>
  );
}
